import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { exchangeTicketAsync, hasPendingExchangeTicket, checkAndConsumeUrlToken } from '../utils/auth';

// const API_URL = 'http://localhost:6001/api'; // Local development
const API_URL = process.env.REACT_APP_API_URL || 'https://app.adsmash.org/api'; // Live URL

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user function
  const loadUser = useCallback(async () => {
    // If an exchange ticket is present in the URL, await ticket redemption first
    if (hasPendingExchangeTicket()) {
      try {
        setLoading(true);
        await exchangeTicketAsync();
      } catch (ticketErr) {
        console.error('Error exchanging ticket in UserContext:', ticketErr);
      }
    }

    let token = localStorage.getItem('token');
    if (!token) {
      token = checkAndConsumeUrlToken() || localStorage.getItem('token');
    }
    
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/user/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        
        // Store basic subscription info in localStorage
        if (response.data.user.subscription) {
          localStorage.setItem('subscription_status', response.data.user.subscription.status);
          localStorage.setItem('subscription_plan', response.data.user.subscription.plan);
        }
      }
    } catch (err) {
      console.error('Error loading user in context:', err);
      setError('Failed to load user data');
      
      // Clear token if it's invalid
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load user on context initialization
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Listen for token changes (e.g., after login)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        // Token was added or removed, reload user data
        loadUser();
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Also check for token changes in the same window
    // We'll use a custom event that can be dispatched after login
    const handleTokenChange = () => {
      loadUser();
    };
    window.addEventListener('tokenChanged', handleTokenChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenChanged', handleTokenChange);
    };
  }, [loadUser]);
  
  // Function to update user data
  const updateUser = (userData) => {
    setUser(userData);
    
    // Update localStorage with subscription info
    if (userData && userData.subscription) {
      localStorage.setItem('subscription_status', userData.subscription.status);
      localStorage.setItem('subscription_plan', userData.subscription.plan);
    }
  };
  
  // Function to get immediate subscription status from localStorage
  const getImmediateSubscription = () => {
    return {
      status: localStorage.getItem('subscription_status') || 'none',
      plan: localStorage.getItem('subscription_plan') || 'none'
    };
  };
  
  // Function to refresh user data from API
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/user/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        
        // Store basic subscription info in localStorage
        if (response.data.user.subscription) {
          localStorage.setItem('subscription_status', response.data.user.subscription.status);
          localStorage.setItem('subscription_plan', response.data.user.subscription.plan);
        }
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setError('Failed to load user data');
      
      // Clear token if it's invalid
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Function to clear user data (for logout)
  const clearUser = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('subscription_status');
    localStorage.removeItem('subscription_plan');
    setLoading(false);
  };
  
  return (
    <UserContext.Provider 
      value={{ 
        user, 
        loading, 
        error, 
        updateUser, 
        clearUser,
        getImmediateSubscription,
        refreshUser
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider; 